/**
 * Report Pipeline (Stage 6 → Report glue)
 *
 * Chains the orchestrator's results-collector into the report-agent package
 * builder so a single call turns raw simulation evidence into a serializable
 * `report-agent-package/v1` plus its screening summary.
 *
 * This module performs only technical screening aggregation and report
 * assembly. It never evaluates Israel municipal compliance and never asserts
 * approval, certification, or "municipal-ready" status; the screening-only
 * disclaimer is always present so that intent is never lost downstream.
 */

import { collectStudyResults, type CollectedStudyResults } from '@enersu/core-orchestrator';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';

import {
  buildReportAgentPackage,
  reportAgentPackageToDocument,
  type ReportAgentPackage,
} from './report-package';
import type { HebrewReportMessages } from './report-shell';

/**
 * Default disclaimer applied when a caller does not supply one. Mirrors the
 * collector's screening-only contract so the package can never silently drop
 * the "evidence only, not compliance" intent.
 */
export const SCREENING_ONLY_DISCLAIMER =
  'Screening evidence only. This package does not assert municipal compliance, approval, or certification.';

export interface BuildReportPackageFromEvidenceInput {
  /**
   * Raw evidence from any source (Forma-native screening, engine fixtures, or
   * deserialized JSON). Each entry is validated by the collector via
   * `parseSimulationEvidence`, so malformed evidence fails fast.
   */
  evidence: readonly unknown[];
  project: { name: string; municipalityId: string; timezone: string };
  municipalityId: string;
  language: 'he' | 'en';
  generatedAt: Date;
  /**
   * Optional override. Defaults to {@link SCREENING_ONLY_DISCLAIMER} so the
   * screening-only intent is preserved even when callers omit it.
   */
  disclaimer?: string;
}

export interface ReportPackageFromEvidenceResult {
  /** Serializable `report-agent-package/v1`, ready for rendering or storage. */
  package: ReportAgentPackage;
  /** Normalized collector output: canonically-ordered studies plus summary. */
  collected: CollectedStudyResults;
}

/**
 * Collects and normalizes raw simulation evidence, then assembles a
 * `report-agent-package/v1` from the resulting studies.
 *
 * The studies in the returned package are exactly the collector's
 * de-duplicated, canonically-ordered set, so report ordering matches the
 * pipeline's study grouping. The collector summary is returned alongside for
 * report headers and pipeline dashboards.
 */
export function buildReportPackageFromEvidence(
  input: BuildReportPackageFromEvidenceInput,
): ReportPackageFromEvidenceResult {
  const collected = collectStudyResults({ evidence: input.evidence });

  const reportPackage = buildReportAgentPackage({
    project: input.project,
    municipalityId: input.municipalityId,
    language: input.language,
    generatedAt: input.generatedAt,
    studies: collected.studies,
    disclaimer: input.disclaimer ?? SCREENING_ONLY_DISCLAIMER,
  });

  return { package: reportPackage, collected };
}

export interface BuildReportDocumentFromEvidenceInput extends BuildReportPackageFromEvidenceInput {
  /** Hebrew RTL report strings used to render the package into a document. */
  messages: HebrewReportMessages;
}

export interface ReportDocumentFromEvidenceResult extends ReportPackageFromEvidenceResult {
  /** Render-ready pdfmake document for the assembled package. */
  document: TDocumentDefinitions;
}

/**
 * One-call path from raw simulation evidence to a render-ready Hebrew RTL
 * pdfmake document: collects and normalizes the evidence, assembles the
 * `report-agent-package/v1`, then renders it via the shared report shell.
 *
 * Returns the document alongside the package and collector output so callers
 * can render, persist, and surface the screening summary from a single call.
 */
export function buildReportDocumentFromEvidence(
  input: BuildReportDocumentFromEvidenceInput,
): ReportDocumentFromEvidenceResult {
  const { messages, ...packageInput } = input;
  const built = buildReportPackageFromEvidence(packageInput);
  const document = reportAgentPackageToDocument(built.package, messages);

  return { ...built, document };
}
